/**
 * Admin V3 — Operations / Health — top-level page
 *
 * Mounted at: /admin-v3/operations/health (see AdminV3.js)
 *
 * GOVERNANCE MEANING: System health monitoring (read-only).
 * Mirror of legacy SystemHealthPanel (AdminControlTower.js:1073-1186) at the
 * same 60-second polling cadence (owner ruling #1, Phase 2.5).
 *
 * SCOPE FOR PHASE 2.8:
 *   - GET /admin/control-tower/health/extended every 60 seconds
 *   - Overall status banner (healthy / degraded)
 *   - Per-service cards (MongoDB, Redis) with status + connected + error
 *   - Metrics grid: queue stats, payment errors 24h, active bookings, open incidents
 *   - Drill-down links where targets exist (active_bookings → Live;
 *     open_incidents → Incidents placeholder, degrades gracefully)
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No restart, queue purge, force-sync, or any other admin actions.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertCircle, AlertTriangle, Euro, RotateCcw, Inbox, Loader2, XCircle, Clock, RefreshCcw, ExternalLink, ListChecks } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  cn,
  SectionCard,
  SmallPill,
  overallStatusMeta,
} from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import OperationsHealthServiceCard from './OperationsHealthServiceCard';

const HEALTH_REFETCH_INTERVAL_MS = 60000;  // mirror legacy AdminControlTower.js:1094

const formatTime = (iso, locale) => {
  if (!iso) return '—';
  try {
    const d = typeof iso === 'string' ? new Date(iso) : iso;
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'fr-FR', {
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).format(d);
  } catch {
    return String(iso);
  }
};

// Single metric block — used for queue / payment errors / active bookings / open incidents.
const MetricBlock = ({ icon: Icon, label, value, alertTone = false, link, testId }) => {
  const inner = (
    <>
      <div className="flex items-center gap-2 mb-2">
        <div className={cn('flex h-7 w-7 items-center justify-center rounded-lg', alertTone ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-500')}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</span>
        {link && <ExternalLink className="ml-auto h-3 w-3 text-slate-300" />}
      </div>
      <div className={cn('text-2xl font-bold', alertTone ? 'text-rose-700' : 'text-slate-900')}>{value}</div>
    </>
  );

  const cardClass = cn(
    'rounded-2xl border bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.04)] block',
    alertTone ? 'border-rose-200' : 'border-slate-200',
    link && 'hover:bg-slate-50/60 transition-colors'
  );

  if (link) {
    return (
      <Link to={link} className={cardClass} data-testid={testId}>
        {inner}
      </Link>
    );
  }
  return (
    <div className={cardClass} data-testid={testId}>
      {inner}
    </div>
  );
};

const OperationsHealthPage = () => {
  const locale = DEFAULT_LOCALE;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/control-tower/health/extended',
    {},
    { refetchInterval: HEALTH_REFETCH_INTERVAL_MS }
  );

  const payload = data && typeof data === 'object' ? data : {};
  const services = payload.services || {};
  const metrics = payload.metrics || {};
  const queue = metrics.queue || { pending: 0, processing: 0, failed: 0 };
  const paymentErrors = metrics.payment_errors_24h ?? 0;
  const activeBookings = metrics.active_bookings ?? 0;
  const openIncidents = metrics.open_incidents ?? 0;
  const overallKey = payload.overall || 'healthy';
  const overallMeta = overallStatusMeta[overallKey] || overallStatusMeta.healthy;

  // Skeleton only on first fetch
  const showSkeleton = loading && !data;

  // Error banner (initial fetch only — polling keeps last-good)
  const errorBanner = error && !data ? (
    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3" data-testid="adminv3-operations-health-error">
      <div className="flex items-start gap-3">
        <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-rose-700">{t('operations.health.error.title', locale)}</p>
          <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refetch}
          className="rounded-lg border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
          data-testid="adminv3-operations-health-retry"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
          {t('operations.health.error.retry', locale)}
        </Button>
      </div>
    </div>
  ) : null;

  return (
    <SectionCard
      title={t('operations.health.title', locale)}
      icon={Activity}
      iconClass="bg-emerald-100 text-emerald-600"
      action={
        payload.timestamp && (
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {t('operations.health.updatedAt', locale).replace('{t}', formatTime(payload.timestamp, locale))}
          </span>
        )
      }
    >
      <div className="space-y-5">
        {errorBanner}

        {/* Overall banner */}
        {!showSkeleton && data && (
          <div
            className={cn(
              'rounded-2xl border p-4 flex items-center justify-between',
              overallMeta.alert ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'
            )}
            data-testid="adminv3-operations-health-overall"
          >
            <div className="flex items-center gap-3">
              {overallMeta.alert ? (
                <AlertTriangle className="h-5 w-5 text-rose-600" />
              ) : (
                <RefreshCcw className="h-5 w-5 text-emerald-600" />
              )}
              <SmallPill meta={overallMeta} />
            </div>
          </div>
        )}

        {/* Services grid */}
        <div>
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {t('operations.health.services.title', locale)}
          </h3>
          {showSkeleton ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
              <div className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <OperationsHealthServiceCard
                serviceKey="mongodb"
                serviceName={t('operations.health.services.mongodb', locale)}
                status={services.mongodb}
                locale={locale}
              />
              <OperationsHealthServiceCard
                serviceKey="redis"
                serviceName={t('operations.health.services.redis', locale)}
                status={services.redis}
                locale={locale}
              />
            </div>
          )}
        </div>

        {/* Metrics grid */}
        <div>
          <h3 className="text-[13px] font-semibold uppercase tracking-wider text-slate-500 mb-2">
            {t('operations.health.metrics.title', locale)}
          </h3>
          {showSkeleton ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[1,2,3,4].map((i) => <div key={i} className="h-24 rounded-2xl bg-slate-100 animate-pulse" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {/* Queue: three sub-counters in one card */}
              <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_4px_18px_rgba(15,23,42,0.04)]" data-testid="adminv3-operations-health-metric-queue">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                    <Inbox className="h-3.5 w-3.5" />
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                    {t('operations.health.metrics.queue', locale)}
                  </span>
                </div>
                <dl className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <dd className="text-lg font-bold text-slate-900">{queue.pending ?? 0}</dd>
                    <dt className="text-[10px] text-slate-400 uppercase tracking-wider">{t('operations.health.metrics.queuePending', locale)}</dt>
                  </div>
                  <div>
                    <dd className="text-lg font-bold text-slate-900 flex items-center justify-center gap-1">
                      <Loader2 className="h-3 w-3 text-slate-400" />
                      {queue.processing ?? 0}
                    </dd>
                    <dt className="text-[10px] text-slate-400 uppercase tracking-wider">{t('operations.health.metrics.queueProcessing', locale)}</dt>
                  </div>
                  <div>
                    <dd className={cn('text-lg font-bold flex items-center justify-center gap-1', (queue.failed ?? 0) > 0 ? 'text-rose-700' : 'text-slate-900')}>
                      <XCircle className="h-3 w-3" />
                      {queue.failed ?? 0}
                    </dd>
                    <dt className="text-[10px] text-slate-400 uppercase tracking-wider">{t('operations.health.metrics.queueFailed', locale)}</dt>
                  </div>
                </dl>
              </div>

              <MetricBlock
                icon={Euro}
                label={t('operations.health.metrics.paymentErrors', locale)}
                value={paymentErrors}
                alertTone={paymentErrors > 0}
                testId="adminv3-operations-health-metric-payment-errors"
              />

              <MetricBlock
                icon={ListChecks}
                label={t('operations.health.metrics.activeBookings', locale)}
                value={activeBookings}
                link="/admin/operations"
                testId="adminv3-operations-health-metric-active-bookings"
              />

              <MetricBlock
                icon={AlertTriangle}
                label={t('operations.health.metrics.openIncidents', locale)}
                value={openIncidents}
                alertTone={openIncidents > 0}
                link="/admin/incidents"
                testId="adminv3-operations-health-metric-open-incidents"
              />
            </div>
          )}
        </div>
      </div>
    </SectionCard>
  );
};

export default OperationsHealthPage;
